import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFydUser } from '@/store/auth';
import { sessionCreate } from '@/api/m0_sessions';
import { createCheckoutSession } from '@/api/billing';
import { ApiError } from '@/api/client';
import type { SessionListItem } from '@/types/api';
import type { IntentPreset } from '@/types/intent';
import PageHeader from '@/ui/organisms/PageHeader';
import EntitlementCard from '@/ui/organisms/EntitlementCard';
import SessionListCard from '@/ui/organisms/SessionListCard';
import WelcomeCard from '@/ui/organisms/WelcomeCard';
import LoadingState from '@/ui/molecules/LoadingState';
import EmptyState from '@/ui/molecules/EmptyState';
import ErrorState from '@/ui/molecules/ErrorState';
import NewSessionDialog from '@/ui/molecules/NewSessionDialog';
import ErrorInlineWithRetry from '@/components/ErrorInlineWithRetry';
import AttachmentUploader from '@/components/AttachmentUploader';
import { useUIStore } from '@/store/ui';
import { useSessionStore } from '@/store/session';
import { useAhaFlag } from '@/hooks/useAhaFlag';
import { ProgressDashboard } from '@/components/ProgressDashboard';
import { UnlockMilestoneBanner } from '@/components/UnlockMilestoneBanner';
import { useUnlockMilestone, MILESTONE_THRESHOLDS, type MilestoneId } from '@/store/unlockMilestone';
import firstRunCouncilSvg from '@/assets/empty/first_run_council.svg';

// Per EXPERIENCE.md a3 journey + PR-UX-04 4 states + PR-UX-10 Hick.
// Two variants:
//   - First-run (sessions=[], trial_used=false): WelcomeCard above the EntitlementCard,
//     history block hidden — single decision moment.
//   - Returning (sessions ≥ 1 OR trial_used=true): standard EntitlementCard + history.
export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useFydUser((s) => s.me);
  const meError = useFydUser((s) => s.error);

  // FYD-2026-0512 dashboard-history-empty: read history from me.recent_sessions
  // (the inline-history strip emitted by /api/v1/me, BE-GAP-7 Wave 8). The
  // legacy /api/v1/sessions round-trip was unreliable for users with valid
  // auth on /me but silently empty list on /sessions; consolidating onto a
  // single round-trip eliminates the failure mode and cuts dashboard TTI.
  const sessions: SessionListItem[] | null = useMemo(() => {
    if (!me) return null;
    if (!me.recent_sessions) return [];
    return me.recent_sessions.map((r) => ({
      session_id: r.session_id,
      question: r.topic,
      status: r.status,
      created_at: r.created_at,
    }));
  }, [me]);
  const listError = meError;
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // ★ M2.1 W1: structured error for ErrorInlineWithRetry · maps wire code →
  // friendly inline error variant (replaces toast for 7 wire codes).
  const [actionErrorCode, setActionErrorCode] = useState<
    'storage_pressure' | 'topic_not_applicable' | 'attachment_too_large'
    | 'attachment_too_many' | 'content_filter_blocked' | 'url_unsafe' | null
  >(null);
  const [actionErrorTraceId, setActionErrorTraceId] = useState<string | null>(null);
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const pushModal = useUIStore((s) => s.pushModal);

  // Sessions are derived from me.recent_sessions (see useMemo above);
  // no separate fetch needed. The retry button on the history error block
  // page-reloads so the auth bootstrap re-fetches /me from scratch — simple
  // and avoids adding a reload action to the auth store just for this path.
  const loadSessions = () => { window.location.reload(); };

  const onStart = () => {
    setActionError(null);
    setNewSessionOpen(true);
  };

  const onSubmitQuestion = async (payload: {
    topic: string;
    intent_preset?: IntentPreset;
    attachments: import('@/api/m0_sessions').AttachmentInput[];
  }) => {
    const { topic, intent_preset: intentPreset, attachments } = payload;
    setBusy(true);
    setActionError(null);
    try {
      // M0 alignment per F-02 PRD spec: topic ≥50 chars.
      if (topic.trim().length < 50) {
        setActionError('议题需 ≥50 字 — 请展开你想批判的方向（角色/痛点/竞品/场景）');
        setBusy(false);
        return;
      }
      // M0.5 (FE-M3): when the dialog returns a non-'auto' preset, thread it
      // into the body so BE skips the LLM classifier and uses our preset
      // verbatim. 'auto' is encoded by omitting intent_preset.
      const r = await sessionCreate({
        topic,
        ...(intentPreset ? { intent_preset: intentPreset } : {}),
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
      });
      setNewSessionOpen(false);
      if (r.payment_required) {
        pushModal('payment_modal');
        return;
      }
      // ★ Phase 4d FE-FE 联调测试 surfaced bug: must seed the session store
      // with the M2 response (choreography / intent_inferred / attachment_briefing
      // / fallback_used / gating_mode) BEFORE navigating to /sessions/{id}/live.
      // Without this, Live.tsx mounts before SSE delivers any state, falls back
      // to M0 PERSONA_ORDER + omits ChoreographyPhaseStrip + AdvanceButton mount
      // condition (gating_mode==='user_advance') is false → user stuck.
      useSessionStore.getState().initFromSessionCreateResponse(r);
      // Pass sse_url via location state so Live.tsx can subscribe with token.
      navigate(`/sessions/${r.session_id}/live`, { state: { sseUrl: r.sse_url } });
    } catch (err) {
      // Reset structured error on each attempt
      setActionErrorCode(null);
      setActionErrorTraceId(null);
      if (err instanceof ApiError && err.errorCode === 'payment_required') {
        setNewSessionOpen(false);
        pushModal('payment_modal');
      } else if (err instanceof ApiError && err.errorCode === 'wallet_not_connected') {
        pushModal('wallet_connect_modal');
      } else if (err instanceof ApiError && (
        err.errorCode === 'usdt_timeout' ||
        err.errorCode === 'usdt_wrong_amount' ||
        err.errorCode === 'usdt_wrong_network'
      )) {
        setNewSessionOpen(false);
        pushModal('payment_failed_error_modal');
      } else if (err instanceof ApiError && err.status === 402) {
        setActionError(t('dash.err.subscriptionRequired'));
      } else if (err instanceof ApiError && err.status === 403 && err.errorCode === 'tos_not_accepted') {
        setActionError(t('dash.err.tosRequired'));
      } else if (
        err instanceof ApiError &&
        (err.errorCode === 'storage_pressure' ||
          err.errorCode === 'topic_not_applicable' ||
          err.errorCode === 'attachment_too_large' ||
          err.errorCode === 'attachment_too_many' ||
          err.errorCode === 'content_filter_blocked' ||
          err.errorCode === 'url_unsafe')
      ) {
        // ★ M2.1 W1: surface inline error w/ ErrorInlineWithRetry (变体含
        // retry / hint / "不会扣费" banner per code · per FE_SPEC §6.1)
        setActionErrorCode(err.errorCode as typeof actionErrorCode);
        setActionErrorTraceId(err.traceId ?? null);
        setActionError(null);  // suppress toast string · use inline instead
      } else {
        setActionError(err instanceof Error ? err.message : 'Failed to start council');
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubscribe = async () => {
    setActionError(null);
    setBusy(true);
    try {
      const r = await createCheckoutSession();
      window.location.href = r.checkout_url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setBusy(false);
    }
  };

  const onFixPayment = () => navigate('/account/billing');

  // Variant detection. Stays "loading" until both me and sessions resolve.
  const sessionsLoaded = sessions !== null;
  const hasHistory = sessionsLoaded && sessions.length > 0;
  const isFirstRun =
    !!me && sessionsLoaded && sessions.length === 0 && !me.trial_used &&
    me.subscription_status === 'inactive';

  const progressFlag = useAhaFlag('FYD_AHA_PROGRESS_REVEAL');
  const milestoneRecords = useUnlockMilestone((s) => s.records);
  const activeMilestone = useMemo<MilestoneId | null>(() => {
    if (!me) return null;
    const ids: MilestoneId[] = ['hundred_councils', 'fifty_councils', 'ten_councils'];
    for (const id of ids) {
      const r = milestoneRecords[id];
      if (me.councils_run >= MILESTONE_THRESHOLDS[id] && !r.dismissedAt) return id;
    }
    return null;
  }, [me, milestoneRecords]);

  return (
    <div className="max-w-page mx-auto px-8 lg:px-16 xl:px-24 py-12">
      <PageHeader
        eyebrow={t('dash.eyebrow')}
        title={isFirstRun ? t('dash.titleFirstRun') : t('dash.title')}
      />

      {progressFlag && activeMilestone && me && (
        <UnlockMilestoneBanner
          milestoneId={activeMilestone}
          councilsRun={me.councils_run}
        />
      )}

      <section className="mb-12">
        {isFirstRun && <WelcomeCard />}

        <EntitlementCard
          me={me}
          busy={busy}
          onStart={onStart}
          onSubscribe={onSubscribe}
          onFixPayment={onFixPayment}
          error={actionError}
        />
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/onboarding" className="text-ink-2 hover:text-ink border-b border-current pb-px">
            {t('dash.completeProfile')}
          </Link>
          <Link
            to="/account/billing"
            className="text-ink-2 hover:text-ink border-b border-current pb-px"
          >
            {t('dash.manageBilling')}
          </Link>
        </div>
      </section>

      {progressFlag && me && me.councils_run >= 1 && (
        <section className="mb-10" data-testid="progress-dashboard-section">
          <ProgressDashboard me={me} />
        </section>
      )}

      {/* History section: only shown when there's something to show OR an error.
          First-run users see no history block — single decision focus.
          FYD-2026-0512: BE caps at 50 newest (older entries pruned on next
          session create); wrap in a max-height scroll container so the
          dashboard doesn't grow taller than the viewport. UI tokens kept
          identical — same Card spacing, no new chrome. */}
      {(hasHistory || listError) && (
        <section>
          <h2 className="font-serif text-2xl font-medium mb-4">{t('dash.history')}</h2>
          {listError ? (
            <ErrorState
              title={t('dash.historyErrorTitle')}
              message={t('dash.historyError')}
              retry={loadSessions}
              retryLabel={t('common.retry')}
            />
          ) : (
            <div
              className="max-h-[640px] overflow-y-auto pr-2"
              data-testid="dash-history-scroll"
            >
              <ul className="space-y-3">
                {(sessions ?? []).map((s) => (
                  <li key={s.session_id}>
                    <SessionListCard session={s} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Loading state: shown while sessions list is in flight (only if not first-run). */}
      {!sessionsLoaded && !listError && (
        <section>
          <h2 className="font-serif text-2xl font-medium mb-4">{t('dash.history')}</h2>
          <LoadingState variant="skeleton" rows={2} label={t('dash.historyLoading')} />
        </section>
      )}

      {/* Returning user with empty history (e.g. all sessions deleted) sees a quiet empty pip. */}
      {sessionsLoaded && !hasHistory && !isFirstRun && (
        <section>
          <h2 className="font-serif text-2xl font-medium mb-4">{t('dash.history')}</h2>
          <EmptyState
            title={t('dash.historyEmptyTitle')}
            sub={t('dash.historyEmpty')}
            illustrationSrc={firstRunCouncilSvg}
            illustrationAlt={t('empty.firstRun.label')}
          />
        </section>
      )}

      <NewSessionDialog
        open={newSessionOpen}
        busy={busy}
        error={actionError}
        inlineErrorNode={
          actionErrorCode ? (
            <ErrorInlineWithRetry
              code={actionErrorCode}
              trace_id={actionErrorTraceId ?? undefined}
              onRetry={
                actionErrorCode === 'storage_pressure'
                  ? () => {
                      setActionErrorCode(null);
                      setActionErrorTraceId(null);
                    }
                  : undefined
              }
              className="mb-3"
            />
          ) : null
        }
        minLength={50}
        onSubmit={onSubmitQuestion}
        onCancel={() => {
          setNewSessionOpen(false);
          setActionErrorCode(null);
          setActionErrorTraceId(null);
        }}
        attachmentSlot={(value, onChange, dialogBusy) => (
          <AttachmentUploader value={value} onChange={onChange} disabled={dialogBusy} />
        )}
      />
    </div>
  );
}
